import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Copy, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const MonthCopyPaste = ({ monthlyData, onUpdate, dataType = 'occupancy' }) => {
  const [copiedValue, setCopiedValue] = useState(null);
  const [copiedMonth, setCopiedMonth] = useState(null);

  const handleCopyMonth = (monthIndex) => {
    const value = monthlyData[monthIndex];
    setCopiedValue(value);
    setCopiedMonth(monthIndex);
    toast.success(`Copied ${MONTHS[monthIndex]} ${dataType} (${value}${dataType === 'occupancy' ? '%' : ''})`);
  };

  const handlePasteToMonth = (monthIndex) => {
    if (copiedValue === null) {
      toast.error('No value copied yet');
      return;
    }
    
    const updatedData = [...monthlyData];
    updatedData[monthIndex] = copiedValue;
    onUpdate(updatedData);
    toast.success(`Pasted to ${MONTHS[monthIndex]}`);
  };

  const handlePasteToAll = () => {
    if (copiedValue === null) {
      toast.error('No value copied yet');
      return;
    }
    
    const updatedData = Array(12).fill(copiedValue);
    onUpdate(updatedData);
    toast.success(`Applied ${copiedValue}${dataType === 'occupancy' ? '%' : ''} to all months`);
  };

  const handlePasteToRange = (startMonth, endMonth) => {
    if (copiedValue === null) {
      toast.error('No value copied yet');
      return;
    }
    
    const updatedData = [...monthlyData];
    for (let i = startMonth; i <= endMonth; i++) {
      updatedData[i] = copiedValue;
    }
    onUpdate(updatedData);
    toast.success(`Applied to ${MONTHS[startMonth]} - ${MONTHS[endMonth]}`);
  };

  return (
    <div className="flex gap-2 items-center">
      {/* Copy from Month */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Copy className="h-4 w-4" />
            Copy From
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>Copy {dataType} from month</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {MONTHS.map((month, index) => (
            <DropdownMenuItem 
              key={index} 
              onClick={() => handleCopyMonth(index)}
              className="flex justify-between"
            >
              <span>{month}</span>
              <span className="text-muted-foreground">
                {monthlyData[index] || 0}{dataType === 'occupancy' ? '%' : ''}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Paste to Month/Range */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-2"
            disabled={copiedValue === null}
          >
            <ArrowRight className="h-4 w-4" />
            Paste To
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>
            Paste {copiedValue !== null && `${copiedValue}${dataType === 'occupancy' ? '%' : ''}`}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={handlePasteToAll}>
            <strong>All Months</strong>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs">Paste to Individual Month</DropdownMenuLabel>
          
          {MONTHS.map((month, index) => (
            <DropdownMenuItem 
              key={index} 
              onClick={() => handlePasteToMonth(index)}
              disabled={copiedMonth === index}
            >
              {month}
            </DropdownMenuItem>
          ))}
          
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs">Paste to Range</DropdownMenuLabel>
          
          <DropdownMenuItem onClick={() => handlePasteToRange(0, 2)}>
            Q1 (Jan-Mar)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePasteToRange(3, 5)}>
            Q2 (Apr-Jun)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePasteToRange(6, 8)}>
            Q3 (Jul-Sep)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePasteToRange(9, 11)}>
            Q4 (Oct-Dec)
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={() => handlePasteToRange(0, 5)}>
            First Half (Jan-Jun)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePasteToRange(6, 11)}>
            Second Half (Jul-Dec)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {copiedValue !== null && (
        <div className="text-sm text-muted-foreground ml-2">
          Copied: <strong>{copiedValue}{dataType === 'occupancy' ? '%' : ''}</strong> 
          {copiedMonth !== null && ` from ${MONTHS[copiedMonth]}`}
        </div>
      )}
    </div>
  );
};

export default MonthCopyPaste;
